/**
 * Internal dependencies
 */
import type {
  LottieColorArray,
  LottieDefinition,
  LottieElement,
  LottieParameter,
  LottieParameterBinding,
  LottieParameterValue,
} from '../types';
import {
  getEffectiveLottieParameterValues,
  LottieContractError,
} from './contract';

type JsonContainer = Record<string, unknown> | unknown[];

interface ResolvedPointer {
  parent: JsonContainer;
  key: string;
  target: unknown;
}

interface SlotMatch {
  target: Record<string, unknown>;
}

const HEX_COLOR_PATTERN = /^#[0-9a-f]{6}([0-9a-f]{2})?$/i;

const fail = (fieldName: string, message: string): never => {
  throw new LottieContractError(message, fieldName);
};

const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isJsonContainer = (value: unknown): value is JsonContainer =>
  isObject(value) || Array.isArray(value);

const isColorArray = (value: unknown): value is LottieColorArray =>
  Array.isArray(value) &&
  (value.length === 3 || value.length === 4) &&
  value.every((item) => typeof item === 'number' && Number.isFinite(item));

const isHexColor = (value: unknown): value is string =>
  typeof value === 'string' && HEX_COLOR_PATTERN.test(value);

const normalizeSlotType = (value?: string): string | undefined =>
  value?.replace(/[-_]/g, '').toLowerCase();

const cloneDefinition = (definition: LottieDefinition): LottieDefinition =>
  JSON.parse(JSON.stringify(definition)) as LottieDefinition;

const decodePointerSegment = (segment: string): string =>
  segment.replace(/~1/g, '/').replace(/~0/g, '~');

const getContainerValue = (
  container: JsonContainer,
  key: string,
  fieldName: string
): unknown => {
  if (Array.isArray(container)) {
    const index = Number(key);
    if (!Number.isInteger(index) || index < 0 || index >= container.length) {
      fail(fieldName, 'JSON Pointer does not resolve in the definition.');
    }

    return container[index];
  }

  if (!Object.prototype.hasOwnProperty.call(container, key)) {
    fail(fieldName, 'JSON Pointer does not resolve in the definition.');
  }

  return container[key];
};

const setContainerValue = (
  container: JsonContainer,
  key: string,
  value: unknown
) => {
  if (Array.isArray(container)) {
    container[Number(key)] = value;
    return;
  }

  container[key] = value;
};

const resolveJsonPointer = (
  definition: LottieDefinition,
  jsonPointer: string,
  fieldName: string
): ResolvedPointer => {
  if (!jsonPointer.startsWith('/')) {
    fail(fieldName, 'JSON Pointer must start with "/".');
  }

  const segments = jsonPointer.slice(1).split('/').map(decodePointerSegment);
  let current: unknown = definition;

  for (let index = 0; index < segments.length - 1; index++) {
    if (!isJsonContainer(current)) {
      fail(fieldName, 'JSON Pointer does not resolve in the definition.');
    }

    current = getContainerValue(
      current as JsonContainer,
      segments[index],
      fieldName
    );
  }

  if (!isJsonContainer(current)) {
    fail(fieldName, 'JSON Pointer does not resolve in the definition.');
  }

  const key = segments[segments.length - 1];
  const parent = current as JsonContainer;
  const target = getContainerValue(parent, key, fieldName);

  return {
    parent,
    key,
    target,
  };
};

const roundColorChannel = (value: number): number =>
  Math.max(0, Math.min(255, Math.round(value * 255)));

const numberToHex = (value: number): string =>
  roundColorChannel(value).toString(16).padStart(2, '0');

const lottieColorArrayToHex = (
  value: LottieColorArray,
  includeAlpha: boolean
): string =>
  `#${numberToHex(value[0])}${numberToHex(value[1])}${numberToHex(value[2])}${
    includeAlpha ? numberToHex(value[3] ?? 1) : ''
  }`;

export const hexToLottieColorArray = (
  value: string,
  target?: unknown
): LottieColorArray => {
  if (!isHexColor(value)) {
    fail('parameterValues', 'Color value must be a hex color.');
  }

  const red = parseInt(value.slice(1, 3), 16) / 255;
  const green = parseInt(value.slice(3, 5), 16) / 255;
  const blue = parseInt(value.slice(5, 7), 16) / 255;
  const shouldIncludeAlpha =
    value.length === 9 || (Array.isArray(target) && target.length === 4);

  if (!shouldIncludeAlpha) {
    return [red, green, blue];
  }

  const alpha =
    value.length === 9
      ? parseInt(value.slice(7, 9), 16) / 255
      : isColorArray(target) && target.length === 4
      ? target[3]
      : 1;

  return [red, green, blue, alpha];
};

const getColorValueForTarget = (
  value: LottieParameterValue,
  target: unknown
): LottieParameterValue => {
  if (Array.isArray(target)) {
    if (typeof value === 'string') {
      return hexToLottieColorArray(value, target);
    }

    if (isColorArray(value)) {
      if (target.length === 4 && value.length === 3) {
        return [
          value[0],
          value[1],
          value[2],
          isColorArray(target) ? target[3] : 1,
        ];
      }

      if (target.length === 3 && value.length === 4) {
        return [value[0], value[1], value[2]];
      }

      return [...value] as LottieColorArray;
    }
  }

  if (typeof target === 'string') {
    if (isColorArray(value)) {
      return lottieColorArrayToHex(value, target.length === 9);
    }

    return value;
  }

  return Array.isArray(value) ? ([...value] as LottieColorArray) : value;
};

const getValueForTarget = (
  parameter: LottieParameter,
  value: LottieParameterValue,
  target: unknown
): LottieParameterValue | string => {
  if (parameter.type === 'color') {
    return getColorValueForTarget(value, target);
  }

  return value;
};

const findSlotMatches = (
  value: unknown,
  slotId: string,
  matches: SlotMatch[]
) => {
  if (Array.isArray(value)) {
    value.forEach((item) => findSlotMatches(item, slotId, matches));
    return;
  }

  if (!isObject(value)) {
    return;
  }

  if (value.sid === slotId) {
    matches.push({
      target: value,
    });
  }

  Object.values(value).forEach((childValue) =>
    findSlotMatches(childValue, slotId, matches)
  );
};

const setTextDocumentEntryValue = (entry: unknown, value: string): boolean => {
  if (!isObject(entry)) {
    return false;
  }

  const style = entry.s;
  if (!isObject(style) || typeof style.t !== 'string') {
    return false;
  }

  style.t = value;
  return true;
};

const setTextDocumentValue = (
  target: Record<string, unknown>,
  value: LottieParameterValue
): boolean => {
  if (typeof value !== 'string') {
    return false;
  }

  const targetValue = target.k;
  if (Array.isArray(targetValue)) {
    return targetValue
      .map((entry) => setTextDocumentEntryValue(entry, value))
      .some(Boolean);
  }

  return setTextDocumentEntryValue(targetValue, value);
};

const applySlotBinding = (
  definition: LottieDefinition,
  binding: Extract<LottieParameterBinding, { kind: 'slot' }>,
  parameter: LottieParameter,
  value: LottieParameterValue,
  fieldName: string
) => {
  const matches: SlotMatch[] = [];
  findSlotMatches(definition, binding.slotId, matches);

  if (!matches.length) {
    fail(fieldName, 'Slot binding does not resolve in the definition.');
  }

  const slotType = normalizeSlotType(binding.slotType);
  matches.forEach(({ target }) => {
    if (
      parameter.type === 'text' &&
      (slotType === 'textdocument' || Array.isArray(target.k)) &&
      setTextDocumentValue(target, value)
    ) {
      return;
    }

    if (Object.prototype.hasOwnProperty.call(target, 'k')) {
      target.k = getValueForTarget(parameter, value, target.k);
      return;
    }

    if (
      parameter.type === 'text' &&
      typeof value === 'string' &&
      typeof target.t === 'string'
    ) {
      target.t = value;
      return;
    }

    fail(fieldName, 'Slot binding target cannot be updated.');
  });
};

const applyJsonPointerBinding = (
  definition: LottieDefinition,
  binding: Extract<LottieParameterBinding, { kind: 'jsonPointer' }>,
  parameter: LottieParameter,
  value: LottieParameterValue,
  fieldName: string
) => {
  const resolved = resolveJsonPointer(
    definition,
    binding.jsonPointer,
    fieldName
  );

  setContainerValue(
    resolved.parent,
    resolved.key,
    getValueForTarget(parameter, value, resolved.target)
  );
};

const applyBinding = (
  definition: LottieDefinition,
  binding: LottieParameterBinding,
  parameter: LottieParameter,
  value: LottieParameterValue,
  fieldName: string
) => {
  if (binding.kind === 'slot') {
    applySlotBinding(definition, binding, parameter, value, fieldName);
    return;
  }

  applyJsonPointerBinding(definition, binding, parameter, value, fieldName);
};

// With no parameter default/override, rendering retains the authored binding.
// Copy validation must read that same text rather than treating it as empty.
export const getAuthoredLottieText = (
  definition: LottieDefinition,
  parameter: LottieParameter
): string => {
  const binding = parameter.bindings[0];
  let target: unknown;
  if (binding.kind === 'jsonPointer') {
    target = resolveJsonPointer(
      definition,
      binding.jsonPointer,
      parameter.key
    ).target;
  } else {
    const matches: SlotMatch[] = [];
    findSlotMatches(definition, binding.slotId, matches);
    target = matches[0]?.target;
  }
  if (typeof target === 'string') return target;
  if (!isObject(target)) return '';
  if (typeof target.t === 'string') return target.t;
  if (typeof target.k === 'string') return target.k;
  const entry = Array.isArray(target.k) ? target.k[0] : target.k;
  return isObject(entry) && isObject(entry.s) && typeof entry.s.t === 'string'
    ? entry.s.t
    : '';
};

export const materializeLottieDefinition = (
  element: Pick<
    LottieElement,
    | 'definition'
    | 'parameterSchema'
    | 'defaultParameterValues'
    | 'parameterValues'
  >
): LottieDefinition => {
  const definition = cloneDefinition(element.definition);
  const schema = element.parameterSchema;

  if (!schema?.parameters?.length) {
    return definition;
  }

  const values = getEffectiveLottieParameterValues(element);
  schema.parameters.forEach((parameter, parameterIndex) => {
    if (!Object.prototype.hasOwnProperty.call(values, parameter.key)) {
      return;
    }

    const value = values[parameter.key];
    parameter.bindings.forEach((binding, bindingIndex) =>
      applyBinding(
        definition,
        binding,
        parameter,
        value,
        `parameterSchema.parameters[${parameterIndex}].bindings[${bindingIndex}]`
      )
    );
  });

  return definition;
};
